import * as React from "react";
import { Modal, notification } from "antd";
import { useFormContext } from "react-hook-form";
import { NGLabelText } from "../../../newcomponents/text/labelText";
import { FormDryRunAlert } from "./formDryRunAlert";
import { DomainDryRun } from "./domainDryRun";
import { isApex, parsedDomain } from "../../../services/utils";
import { NGButton } from "../../../newcomponents/button/Button";
import { homeLink, request } from "../../../services/cpln";
import { v4 as uuidv4 } from "uuid";
import { FormNGInput } from "./components/FormNGInput";
import { DomainInput } from "./types/input";

export const CreateNGAdvancedName: React.FC = () => {
  const { control, watch, setValue } = useFormContext<DomainInput>();

  const dryRun = watch("dryRun.raw.value") || {};
  const dryRunNameOnly = watch("dryRun.nameOnly.value") || {};
  const dryRunApex = watch("dryRun.apex.value") || {};

  const isEdit = watch("isEdit") || false;
  const name = watch("advanced.name") || "";
  const isDomainApex = React.useMemo(() => {
    return isApex(name);
  }, [name]);

  React.useEffect(() => {
    if (isDomainApex) {
      setValue("advanced.spec.dnsMode", "cname");
    }
  }, [isDomainApex, setValue]);

  const [isApexLoading, setIsApexLoading] = React.useState(false);
  const [showApexModal, setShowApexModal] = React.useState(false);

  async function createApex() {
    try {
      setIsApexLoading(true);
      await request({
        method: "post",
        url: homeLink("domain"),
        body: { name: parsedDomain(name).domain },
      });
      notification.success({ message: "Created Apex Domain" });
      setIsApexLoading(false);
      setShowApexModal(false);
      setValue("dryRun.triggerer", uuidv4());
    } catch (e) {
      setIsApexLoading(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed to Create Apex Domain", description: errorMessage });
    }
  }

  return (
    <>
      {isEdit ? (
        <div className="mb-8">
          <NGLabelText className="cursor-default">Domain</NGLabelText>
          <div className="color-default">{name}</div>
        </div>
      ) : (
        <div className="mb-8">
          <div className="flex items-center">
            <NGLabelText className="cursor-default">Domain</NGLabelText>
            <span className="color-danger">*</span>
          </div>
          <FormNGInput
            name="advanced.name"
            control={control}
            rules={{ required: "Domain is required" }}
            style={{ width: 600 }}
          />
          <FormDryRunAlert path={"name"} />
        </div>
      )}
      {isEdit ? null : dryRunNameOnly.status === 200 ? null : <DomainDryRun dryRunPath="nameOnly" showValid={true} />}
      {dryRunNameOnly.status === 200 && dryRun.code === "apex_must_exist" ? (
        <div className="mb-8">
          <DomainDryRun
            dryRunPath="raw"
            showValid={true}
            onCreateApex={() => {
              setValue("dryRun.triggerer", uuidv4());
              setShowApexModal(true);
            }}
          />
        </div>
      ) : null}
      {dryRunNameOnly.status === 200 ? (
        <div className="mb-8">
          <NGLabelText>Description</NGLabelText>
          <FormNGInput name="advanced.description" control={control} style={{ width: 600 }} />
          <FormDryRunAlert path={"description"} />
        </div>
      ) : null}
      {showApexModal ? (
        <Modal
          open={showApexModal}
          title={"Create Apex Domain"}
          width={dryRunApex.status !== 200 ? 1000 : undefined}
          onCancel={() => setShowApexModal(false)}
          footer={
            <div className="modal-actions">
              <NGButton variant="secondary" disabled={isApexLoading} onClick={() => setShowApexModal(false)}>
                Cancel
              </NGButton>
              <NGButton variant="primary" loading={isApexLoading} disabled={isApexLoading} onClick={() => createApex()}>
                Create Apex
              </NGButton>
            </div>
          }
        >
          <div>
            <div className="flex flex-col gap-1 mb-4">
              <NGLabelText>Domain</NGLabelText>
              <div>{parsedDomain(name).domain}</div>
            </div>
            <DomainDryRun dryRunPath="apex" showValid={false} />
          </div>
        </Modal>
      ) : null}
    </>
  );
};
